<template>
  <section class="w-full py-12">
    <div class="container px-4 md:px-6">
      <div class="flex flex-col gap-6">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-3xl font-bold tracking-tight">Featured Vehicles</h2>
            <p class="text-muted-foreground">Explore our selection of quality vehicles</p>
          </div>
          <NuxtLink to="/vehicles" class="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
            View All
          </NuxtLink>
        </div>
        
        <!-- Debug Info - Wrapped in ClientOnly to prevent hydration mismatch -->
        <!-- <ClientOnly>
          <div class="bg-muted p-2 text-sm mb-2">
            <p>Store Loading: {{ isStoreLoading }}</p>
            <p>Component Ready: {{ isReady }}</p>
            <p>Vehicles count: {{ vehicleCount }}</p>
            <p>Filtered vehicles: {{ filteredCount }}</p>
            <p>Store initialized: {{ isStoreInitialized }}</p>
          </div>
        </ClientOnly> -->
        
        <!-- Vehicle Cards Grid -->
        <div v-if="isStoreLoading" class="py-12 flex justify-center">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
        
        <div v-else-if="!hasVehicles" class="py-12 text-center">
          <p class="text-muted-foreground">No vehicles found.</p>
        </div>
        
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="vehicle in displayedVehicles" :key="vehicle.id" class="group flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xs transition-all hover:shadow-md">
            <div class="aspect-video relative overflow-hidden">
              <img 
                :src="vehicle.thumb || vehicle.photos?.[0] || '/placeholder-car.jpg'" 
                :alt="vehicle.title || 'Vehicle'" 
                class="h-full w-full object-cover transition-transform group-hover:scale-105"
              >
              <div class="absolute inset-0 bg-linear-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div class="absolute bottom-4 left-4 right-4 flex justify-between items-center">
                  <div class="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      class="text-xs"
                      @click="toggleFavorite(vehicle.id)"
                    >
                      <span v-if="isFavorite(vehicle.id)" class="text-red-500">♥</span>
                      <span v-else>♡</span>
                    </Button>
                    <ClientOnly>
                      <Button
                        variant="secondary"
                        size="sm"
                        class="text-xs"
                        :class="{ '!bg-blue-100 dark:!bg-blue-900/30 !text-blue-600 dark:!text-blue-400': cartStore.isInCart(vehicle.id) }" 
                        @click="addToCart(vehicle.id)"
                        :title="cartStore.isInCart(vehicle.id) ? 'In Cart' : 'Add to cart'"
                      >
                        <ShoppingCartIcon 
                          class="h-4 w-4" 
                          :class="cartStore.isInCart(vehicle.id) ? 'text-blue-600 dark:text-blue-400' : ''" 
                        />
                      </Button>
                    </ClientOnly>
                  </div>
                  <NuxtLink 
                    :to="`/vehicles/${vehicle.slug || vehicle.id}`"
                    class="rounded-md px-3 py-1.5 text-xs font-medium text-white bg-primary shadow-xs hover:bg-primary/90 transition"
                  >
                    View Details
                  </NuxtLink>
                </div>
              </div>
              
              <!-- Badges -->
              <div class="absolute top-2 left-2 flex flex-wrap gap-1">
                <span 
                  v-if="vehicle.condition?.displayValue?.[0]" 
                  class="rounded-full px-2 py-0.5 text-xs font-medium bg-primary/80 text-white"
                >
                  {{ vehicle.condition.displayValue[0] }}
                </span>
              </div>
            </div>
            
            <!-- Vehicle Details -->
            <div class="flex flex-1 flex-col p-4">
              <div class="mb-2 flex items-center justify-between">
                <div>
                  <h3 class="font-semibold text-lg line-clamp-1 group-hover:text-primary transition-colors">
                    {{ vehicle.title || 'Unnamed Vehicle' }}
                  </h3>
                  <div class="text-sm text-muted-foreground">
                    {{ vehicle.make?.displayValue?.[0] || '' }} {{ vehicle.model?.displayValue?.[0] || '' }}
                  </div>
                </div>
                <span class="font-bold text-lg">
                  ${{ formatPrice(vehicle.price || 0) }}
                </span>
              </div>
              
              <div class="grid grid-cols-2 gap-2 mt-2">
                <div v-if="vehicle.kms !== undefined" class="flex items-center gap-1 text-sm text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-gauge"><path d="m12 14 4-4"></path><path d="M3.34 19a10 10 0 1 1 17.32 0"></path></svg>
                  {{ formatKms(vehicle.kms) }} km
                </div>
                <div v-if="vehicle.transmission?.displayValue?.[0]" class="flex items-center gap-1 text-sm text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-cog"><path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"></path><path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"></path><path d="M12 2v2"></path><path d="M12 22v-2"></path><path d="m17 20.66-1-1.73"></path><path d="M11 10.27 7 3.34"></path><path d="m20.66 17-1.73-1"></path><path d="m3.34 7 1.73 1"></path><path d="M14 12h8"></path><path d="M2 12h2"></path><path d="m20.66 7-1.73 1"></path><path d="m3.34 17 1.73-1"></path><path d="m17 3.34-1 1.73"></path><path d="m7 20.66 1-1.73"></path></svg>
                  {{ vehicle.transmission.displayValue[0] }}
                </div>
                <div v-if="vehicle.fuel?.displayValue?.[0]" class="flex items-center gap-1 text-sm text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-fuel"><path d="M3 22h12"></path><path d="M12 16H6a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2h6"></path><path d="M17 22V9"></path><path d="M9 8h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-6"></path><path d="M9 12h7"></path><path d="M16 12v4"></path></svg>
                  {{ vehicle.fuel.displayValue[0] }}
                </div>
                <div v-if="vehicle.body?.displayValue?.[0]" class="flex items-center gap-1 text-sm text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-car"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.6-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C1.4 11.3 1 12.1 1 13v3c0 .6.4 1 1 1h2"></path><circle cx="7" cy="17" r="2"></circle><path d="M9 17h6"></path><circle cx="17" cy="17" r="2"></circle></svg>
                  {{ vehicle.body.displayValue[0] }}
                </div>
              </div>
              
              <div class="mt-4 flex items-center justify-between">
                <div class="text-sm text-muted-foreground">
                  {{ vehicle.sellername || 'Vehicle Dealer' }}
                </div>
                <div v-if="vehicle.address?.suburb && vehicle.address?.state" class="text-sm text-muted-foreground">
                  {{ vehicle.address.suburb }}, {{ vehicle.address.state }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Show more button -->
        <div v-if="hasMoreVehicles" class="flex justify-center mt-6">
          <Button variant="outline" @click="loadMoreVehicles">
            Load More Vehicles
          </Button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
import { useFavoritesStore } from '~/stores/favorites'
import { useCartStore } from '~/stores/cartStore'
import { Button } from '~/layers/base/components/ui/button'
import { ShoppingCartIcon } from '@heroicons/vue/24/outline'

const isReady = ref(false)
const vehiclesStore = useVehiclesStore()
const favoritesStore = useFavoritesStore()
const cartStore = useCartStore()
const visibleCount = ref(6)

// Use computed getters with null checks to avoid directly accessing store properties
const isStoreInitialized = computed(() => vehiclesStore.isDataLoaded)
const isStoreLoading = computed(() => vehiclesStore.isLoading)
const vehicleCount = computed(() => vehiclesStore.vehicles.length)
const filteredVehicles = computed(() => {
  if (!vehiclesStore.isDataLoaded) return []
  return vehiclesStore.filteredVehicles
})
const filteredCount = computed(() => filteredVehicles.value.length || 0)
const hasVehicles = computed(() => filteredCount.value > 0)
const displayedVehicles = computed(() => {
  return filteredVehicles.value.slice(0, visibleCount.value)
})
const hasMoreVehicles = computed(() => visibleCount.value < filteredCount.value)

// Methods with null checks
function formatPrice(price) {
  if (typeof price === 'string') {
    price = parseFloat(price.replace(/[^\d.-]/g, ''))
    if (isNaN(price)) return 'Price on Request'
  }
  return price?.toLocaleString() || '0'
}

function formatKms(kms) {
  if (kms === undefined) return '0'
  return kms?.toLocaleString() || '0'
}

function toggleFavorite(vehicleId) {
  if (vehicleId) {
    favoritesStore.toggleFavorite(vehicleId)
  }
}

function isFavorite(vehicleId) {
  if (!vehicleId) return false;
  return favoritesStore.isFavorite(vehicleId)
}

function loadMoreVehicles() {
  visibleCount.value += 6
}

function addToCart(vehicleId) {
  if (!vehicleId) {
    console.error("Cannot add to cart: Vehicle ID missing");
    return;
  }
  cartStore.addToCart(vehicleId);
}

// Only access store methods after ensuring initialization
onMounted(() => {
  isReady.value = true
  if (!vehiclesStore.isDataLoaded) {
    vehiclesStore.fetchVehicles().catch(err => console.error('[FeaturedVehiclesDisplay] Error fetching vehicles:', err))
  }
})
</script> 